import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchCategories } from "../redux/productSlice";
import { logoutUser } from "../redux/authSlice";
import { BsList } from "react-icons/bs";
import { ShoppingCart } from "lucide-react";
import {
  FaSearch,
  FaQrcode,
  FaUser,
  FaChevronDown,
  FaGlobe,
} from "react-icons/fa";
import usePWAInstallPrompt from "@/hooks/usePWAInstallPrompt";
import Sidebar from "./Sidebar";
import "bootstrap/dist/css/bootstrap.min.css";
import useCurrencyConverter from "@/components/CurrencyConvertor";
import { currencyOptions } from "@/utils/currencyMeta";
import useDynamicPromoIcon from "@/hooks/useDynamicPromoIcon";
const API_BASE_URL = process.env.REACT_APP_API_URL;
/* ---------------------------------------------
 ✅ Save search history (localStorage + cookies)
----------------------------------------------*/
const addSearchTerm = (term) => {
  if (!term || term.trim().length < 2) return;

  const key = "recent_searches";

  let searches = JSON.parse(localStorage.getItem(key) || "[]");

  // 🧠 Remove duplicates + move to front
  searches = [
    term,
    ...searches.filter((s) => s.toLowerCase() !== term.toLowerCase()),
  ];

  // 🔒 Limit to last 10 searches
  searches = searches.slice(0, 10);

  localStorage.setItem(key, JSON.stringify(searches));

  // 🍪 Optional cookie (backend usage)
  document.cookie = `${key}=${encodeURIComponent(
    JSON.stringify(searches)
  )}; path=/; max-age=${7 * 24 * 60 * 60}`;
};

// ✅ Save recently viewed products (localStorage)
const saveRecentlyViewed = (product) => {
  if (!product) return;

  const key = "recent_products";

  let items = JSON.parse(localStorage.getItem(key) || "[]");

  items = [
    product,
    ...items.filter((p) => p.id !== product.id),
  ];

  items = items.slice(0, 10);

  localStorage.setItem(key, JSON.stringify(items));
};

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, loading: authLoading } = useSelector((state) => state.auth || {});
  const cartItems = useSelector((state) => state.cart?.cartItems || []);

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState({
  products: [],
  categories: [],
});
const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const currencyRef = useRef(null);
  const profileRef = useRef(null);

  const { promoIconUrl, promoText, isLoading: promoLoading } = useDynamicPromoIcon();
  const { isInstallable, promptInstall } = usePWAInstallPrompt();
  const { currency: selectedCurrency, loading: currencyLoading, switchCurrency, } = useCurrencyConverter();


  const currentCurrencyCode =
    typeof selectedCurrency === "object"
      ? selectedCurrency.code
      : selectedCurrency || "USD";

  const currentCurrencyObj =
    currencyOptions.find((opt) => opt.code === currentCurrencyCode) || {
      code: currentCurrencyCode,
    };

  const cartCount = cartItems.reduce((sum, it) => sum + (it.quantity || 0), 0);

  /* ---------------------------------------------
   ✅ Fetch categories on mount
  ----------------------------------------------*/
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  /* ---------------------------------------------
   ✅ Filter search suggestions
  ----------------------------------------------*/
useEffect(() => {
  if (!search || search.trim().length < 2) {
    setSuggestions({ products: [], categories: [] });
    return;
  }

  const controller = new AbortController();
  const delay = setTimeout(async () => {
    try {
      setLoadingSuggestions(true);
      const res = await fetch(
        `${API_BASE_URL}/products/search/suggestions?q=${encodeURIComponent(search)}`,
        { signal: controller.signal }
      );
      const data = await res.json();
      setSuggestions({
        products: data.products || [],
        categories: data.categories || [],
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Search suggestion error", err);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, 300); // debounce

  return () => {
    clearTimeout(delay);
    controller.abort();
  };
}, [search]);


  /* ---------------------------------------------
   ✅ Handle outside click to close dropdowns
  ----------------------------------------------*/
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target))
        setShowCurrencyMenu(false);
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfileMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------------------------------------
   ✅ Logout user
  ----------------------------------------------*/
  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/");
  };

  /* ---------------------------------------------
   ✅ Currency switch
  ----------------------------------------------*/
  const handleCurrencySelect = (code) => {
  switchCurrency(code);          // ✅ reactive
  setShowCurrencyMenu(false);    // ✅ UI update
};

  /* ---------------------------------------------
   ✅ Handle search submission
  ----------------------------------------------*/
  const handleSearchSubmit = (e) => {
  e.preventDefault();
  if (!search.trim()) return;

  addSearchTerm(search);
  navigate(`/shop?query=${encodeURIComponent(search)}`);

  setSearch("");
  setSuggestions({ products: [], categories: [] });
  setShowMobileSearch(false);
};


  /* ---------------------------------------------
   ✅ Dropdown open handlers (prevent premature close)
  ----------------------------------------------*/
  const toggleCurrencyMenu = (e) => {
    e.stopPropagation();
    setShowCurrencyMenu((p) => !p);
    setShowProfileMenu(false);
  };

  const toggleProfileMenu = (e) => {
    e.stopPropagation();
    setShowProfileMenu((p) => !p);
    setShowCurrencyMenu(false);
  };

  /* ---------------------------------------------
   ✅ Auto-close helper for dropdowns
  ----------------------------------------------*/
  const handleMenuLinkClick = (callback) => {
    setShowProfileMenu(false);
    setShowCurrencyMenu(false);
    if (callback) callback();
  };

  const safeAreaStyle = {
    paddingTop: "env(safe-area-inset-top)",
  };

  /* ---------------------------------------------
   ✅ Navbar render
  ----------------------------------------------*/
  return (
    <>

          {/* ================= MOBILE NAVBAR ================= */}
      <header
        className="sticky-top d-lg-none"
        style={{
          background: "rgba(180,0,0,0.95)",
          borderBottom: "2px solid #b30000",
          zIndex: 1200,
          ...safeAreaStyle,
        }}
      >
        <nav className="navbar px-3 py-2 d-flex align-items-center justify-content-between">
          <button
            className="btn btn-sm btn-outline-light"
            onClick={() => setIsSidebarOpen(true)}
          >
            <BsList size={22} />
          </button>

          <Link to="/" className="navbar-brand text-white fw-bold m-0">
            vikad
          </Link>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-light"
              onClick={() => setShowMobileSearch((p) => !p)}
            >
              <FaSearch />
            </button>

            {/* Currency Dropdown */}
              <div ref={currencyRef} className="position-relative">
                <button
                  className="btn btn-sm btn-outline-light d-flex align-items-center px-2 py-1"
                  onClick={toggleCurrencyMenu}
                >
                  <FaGlobe className="me-1" size={13} />
                  {currencyLoading ? "..." : currentCurrencyObj.code}
                  <FaChevronDown size={10} className="ms-1" />
                </button>

                {showCurrencyMenu && (
                  <div
                    className="position-absolute shadow-sm rounded p-1"
                    style={{
                      right: 0,
                      top: "100%",
                      background: "rgba(180,0,0,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      maxHeight: 180,
                      overflowY: "auto",
                      minWidth: 110,
                      fontSize: "0.8rem",
                      zIndex: 1300,
                    }}
                  >
                    {currencyOptions.map((opt) => (
                      <button
                        key={opt.code}
                        className="dropdown-item d-flex align-items-center text-light small py-1 w-100"
                        style={{ background: "transparent", border: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCurrencySelect(opt.code);
                        }}
                      >
                        {opt.flag ? (
                          <img
                            src={`https://flagcdn.com/w20/${opt.flag}.png`}
                            alt={opt.code}
                            className="me-2"
                          />
                        ) : (
                          <FaGlobe className="me-2" />
                        )}
                        {opt.code}
                      </button>
                    ))}
                  </div>
                )}
              </div>

            {/* Profile Dropdown */}
              <div ref={profileRef} className="position-relative">
                <button
                  className="btn btn-sm btn-outline-light d-flex align-items-center px-2 py-1"
                  onClick={toggleProfileMenu}
                >
                  <FaUser className="me-1" size={13} />
                  <span className="d-none d-md-inline" style={{ fontSize: 13 }}>
                    {user ? user.name : "Account"}
                  </span>
                  <FaChevronDown size={10} className="ms-1" />
                </button>

                {showProfileMenu && (
                  <div
                    className="position-absolute shadow-sm rounded p-1"
                    style={{
                      right: 0,
                      top: "100%",
                      background: "rgba(150,0,0,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      minWidth: 130,
                      zIndex: 1300,
                    }}
                  >
                    {user ? (
                      <>
                        <Link
                          to="/orders"
                          className="dropdown-item text-light small py-1"
                          onClick={() =>
                            handleMenuLinkClick(() => navigate("/orders"))
                          }
                        >
                          Orders
                        </Link>
                        <hr
                          className="my-1"
                          style={{
                            borderColor: "rgba(255,255,255,0.1)",
                          }}
                        />
                        <button
                          className="dropdown-item text-warning small py-1 w-100"
                          onClick={() => handleMenuLinkClick(handleLogout)}
                          disabled={authLoading}
                        >
                          {authLoading ? "Logging out..." : "Logout"}
                        </button>
                      </>
                    ) : (
                      <Link
                        to="/login"
                        className="dropdown-item text-light small py-1"
                        onClick={() =>
                          handleMenuLinkClick(() => navigate("/login"))
                        }
                      >
                        Login
                      </Link>
                    )}
                  </div>
                )}
              </div>

            <button
              className="btn btn-sm btn-outline-light position-relative"
              onClick={() => navigate("/cart")}
            >
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="badge bg-light text-danger position-absolute top-0 start-100 translate-middle">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </nav>

        {showMobileSearch && (
          <form className="px-3 pb-2" onSubmit={handleSearchSubmit}>
            <input
              className="form-control form-control-sm"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>
        )}
      </header>
      
      {/* ---------------- DESKTOP NAVBAR ---------------- */}
      <header
        className="sticky-top d-none d-lg-block"
        style={{
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(180, 0, 0, 0.9)",
          borderBottom: "2px solid #b30000",
          zIndex: 1100,
        }}
      >
        <nav className="navbar py-2 px-4">
          <div className="container-fluid d-flex align-items-center justify-content-between">
            {/* LEFT SIDE */}
            <div className="d-flex align-items-center gap-3">
              <button
                className="btn btn-sm btn-outline-light border-light"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                title="Open Sidebar"
              >
                <BsList size={18} />
              </button>

              {/* Brand */}
              <div className="d-flex align-items-center gap-2">
                {promoIconUrl && (
                  <img
                    src={promoIconUrl}
                    alt="Promo Icon"
                    width="30"
                    height="30"
                    style={{
                      borderRadius: "50%",
                      border: "2px solid white",
                      objectFit: "cover",
                      opacity: promoLoading ? 0.6 : 1,
                    }}
                  />
                )}
                <Link
                  to="/"
                  onClick={() => handleMenuLinkClick()}
                  className="navbar-brand text-white fw-bold fs-5 text-decoration-none"
                >
                  vikad
                </Link>
              </div>

              {!promoLoading && promoText && (
                <span
                  className="text-warning fw-semibold ms-2"
                  style={{
                    fontSize: "0.9rem",
                    textShadow: "0 0 5px rgba(255,255,255,0.5)",
                  }}
                >
                  {promoText}
                </span>
              )}
            </div>

            {/* CENTER - Search */}
            <form
              className="d-flex align-items-center position-relative mx-auto flex-grow-1 justify-content-center"
              style={{ maxWidth: 450 }}
              onSubmit={handleSearchSubmit}
            >
              <input
                type="search"
                className="form-control form-control-sm rounded-pill px-3 py-2"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                }}
              />
              <button
                type="submit"
                className="btn btn-sm btn-light ms-2 rounded-circle"
              >
                <FaSearch className="text-danger" />
              </button>

              {(loadingSuggestions ||
  suggestions.products.length > 0 ||
  suggestions.categories.length > 0) && (
  <div
    className="position-absolute mt-2 w-100 shadow rounded"
    style={{
      zIndex: 1200,
      background: "rgba(150,0,0,0.97)",
      maxHeight: 300,
      overflowY: "auto",
    }}
  >
    {/* 🔄 Loading state */}
    {loadingSuggestions && (
      <div className="px-3 py-2 text-light small opacity-75">
        Searching…
      </div>
    )}

    {/* 📦 Products */}
    {!loadingSuggestions &&
      suggestions.products.map((p) => (
        <div
          key={p.id}
          className="px-3 py-2 text-light small"
          style={{ cursor: "pointer" }}
          onClick={() => { 
              addSearchTerm(p.name);
              saveRecentlyViewed(p);
              navigate(`/product/${p.id}`);
              setSearch("");
              setSuggestions({ products: [], categories: [] });
            }}
            >
          📦 {p.name}
        </div>
      ))}

    {/* Divider */}
    {!loadingSuggestions &&
      suggestions.products.length > 0 &&
      suggestions.categories.length > 0 && (
        <hr className="my-1 border-light opacity-25" />
      )}

    {/* 📁 Categories */}
    {!loadingSuggestions &&
      suggestions.categories.map((cat, i) => (
        <div
          key={i}
          className="px-3 py-2 text-warning small"
          style={{ cursor: "pointer" }}
          onClick={() => {
            addSearchTerm(cat);
            localStorage.setItem("last_category", cat.toLowerCase());
            navigate(`/category/${cat.toLowerCase()}`);
            setSearch("");
            setSuggestions({ products: [], categories: [] });
            }}
        >
          📁 {cat}
        </div>
      ))}
  </div>
)}


            </form>

            {/* RIGHT SECTION */}
            <div className="d-flex align-items-center gap-3 ms-auto">
              {/* Install App */}
              {isInstallable && !user && (
                <div
                  className="d-flex align-items-center text-light small"
                  style={{ cursor: "pointer" }}
                  onClick={promptInstall}
                >
                  <FaQrcode size={14} className="me-1" />
                  <span>Download App</span>
                </div>
              )}

              {/* Currency Dropdown */}
              <div ref={currencyRef} className="position-relative">
                <button
                  className="btn btn-sm btn-outline-light d-flex align-items-center px-2 py-1"
                  onClick={toggleCurrencyMenu}
                >
                  <FaGlobe className="me-1" size={13} />
                  {currencyLoading ? "..." : currentCurrencyObj.code}
                  <FaChevronDown size={10} className="ms-1" />
                </button>

                {showCurrencyMenu && (
                  <div
                    className="position-absolute shadow-sm rounded p-1"
                    style={{
                      right: 0,
                      top: "100%",
                      background: "rgba(180,0,0,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      maxHeight: 180,
                      overflowY: "auto",
                      minWidth: 110,
                      fontSize: "0.8rem",
                      zIndex: 1300,
                    }}
                  >
                    {currencyOptions.map((opt) => (
                      <button
                        key={opt.code}
                        className="dropdown-item d-flex align-items-center text-light small py-1 w-100"
                        style={{ background: "transparent", border: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCurrencySelect(opt.code);
                        }}
                      >
                        {opt.flag ? (
                          <img
                            src={`https://flagcdn.com/w20/${opt.flag}.png`}
                            alt={opt.code}
                            className="me-2"
                          />
                        ) : (
                          <FaGlobe className="me-2" />
                        )}
                        {opt.code}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div ref={profileRef} className="position-relative">
                <button
                  className="btn btn-sm btn-outline-light d-flex align-items-center px-2 py-1"
                  onClick={toggleProfileMenu}
                >
                  <FaUser className="me-1" size={13} />
                  <span className="d-none d-md-inline" style={{ fontSize: 13 }}>
                    {user ? user.name : "Account"}
                  </span>
                  <FaChevronDown size={10} className="ms-1" />
                </button>

                {showProfileMenu && (
                  <div
                    className="position-absolute shadow-sm rounded p-1"
                    style={{
                      right: 0,
                      top: "100%",
                      background: "rgba(150,0,0,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      minWidth: 130,
                      zIndex: 1300,
                    }}
                  >
                    {user ? (
                      <>
                        <Link
                          to="/orders"
                          className="dropdown-item text-light small py-1"
                          onClick={() =>
                            handleMenuLinkClick(() => navigate("/orders"))
                          }
                        >
                          Orders
                        </Link>
                        <hr
                          className="my-1"
                          style={{
                            borderColor: "rgba(255,255,255,0.1)",
                          }}
                        />
                        <button
                          className="dropdown-item text-warning small py-1 w-100"
                          onClick={() => handleMenuLinkClick(handleLogout)}
                          disabled={authLoading}
                        >
                          {authLoading ? "Logging out..." : "Logout"}
                        </button>
                      </>
                    ) : (
                      <Link
                        to="/login"
                        className="dropdown-item text-light small py-1"
                        onClick={() =>
                          handleMenuLinkClick(() => navigate("/login"))
                        }
                      >
                        Login
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* Cart */}
              <button
                className="btn btn-sm btn-outline-light position-relative px-2 py-1"
                onClick={() => handleMenuLinkClick(() => navigate("/cart"))}
              >
                <ShoppingCart size={17} />
                {cartCount > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-light text-danger"
                    style={{
                      fontSize: 10,
                      padding: "0.3rem 0.4rem",
                    }}
                  >
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* ---------------- SIDEBAR ---------------- */}
      <Sidebar isOpen={isSidebarOpen} closeSidebar={() => setIsSidebarOpen(false)} />
    </>
  );
};

export default Navbar;